/**
 * Delivery ETA — made-to-order window under the add-to-cart button.
 * Vanilla custom element, no dependencies, no API call.
 *
 * Window = production lead time + courier transit, both counted in business days
 * from Dubai. Production can be anchored to a later batch start date via the
 * bandor.production_start metafield.
 */

(() => {
  if (customElements.get('delivery-eta')) return;

  /* Transit from Dubai, in business days [min, max] */
  const TRANSIT = {
    AE: [1, 2],
    /* GCC */
    SA: [2, 3], KW: [2, 3], QA: [2, 3], BH: [2, 3], OM: [2, 3],
    /* North America */
    US: [4, 6], CA: [4, 6],
    /* Europe + UK */
    GB: [3, 5], FR: [3, 5], DE: [3, 5], IT: [3, 5], ES: [3, 5], NL: [3, 5],
    BE: [3, 5], LU: [3, 5], IE: [3, 5], PT: [3, 5], AT: [3, 5], CH: [3, 5],
    DK: [3, 5], SE: [3, 5], NO: [3, 5], FI: [3, 5], PL: [3, 5], CZ: [3, 5],
    GR: [3, 5], HU: [3, 5], RO: [3, 5], SK: [3, 5], SI: [3, 5], HR: [3, 5],
    BG: [3, 5], EE: [3, 5], LV: [3, 5], LT: [3, 5], CY: [3, 5], MT: [3, 5],
    /* Asia-Pacific hubs */
    SG: [3, 5], HK: [3, 5], JP: [4, 6], KR: [4, 6], AU: [4, 6], NZ: [5, 7],
    IN: [3, 5], CN: [4, 6],
  };

  const DEFAULT_TRANSIT = [5, 8];

  const COPY = {
    range: 'Made to order. Delivery from {min} to {max}',
    single: 'Made to order. Delivery on {min}',
    duties: 'Duties and taxes paid at checkout. Nothing to pay on delivery.',
  };

  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  const toKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;

  const addBusinessDays = (from, days, holidays) => {
    const date = new Date(from.getTime());
    let added = 0;
    let guard = 0;

    while (added < days && guard < 3650) {
      date.setDate(date.getDate() + 1);
      guard++;
      if (!isWeekend(date) && !holidays.has(toKey(date))) added++;
    }

    return date;
  };

  /* Spelled-out month: 09/07 reads as Sep 7 in the US and Jul 9 everywhere else,
     and the store ships worldwide. */
  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const format = (date) => `${MONTHS[date.getMonth()]} ${date.getDate()}`;

  class DeliveryEta extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('.delivery-eta__message');
      this.input = this.querySelector('[data-delivery-eta-input]');
      this.render();
    }

    get holidays() {
      return new Set(
        (this.dataset.holidays || '')
          .split(',')
          .map((day) => day.trim())
          .filter(Boolean)
      );
    }

    /**
     * Production starts today unless the product is tied to a batch that opens
     * later — in which case a buyer ordering on day 2 of the window still gets
     * the same honest dates as one ordering on day 1.
     */
    get startDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const anchor = this.dataset.productionStart;
      if (!anchor) return today;

      const parsed = new Date(`${anchor}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return today;

      return parsed > today ? parsed : today;
    }

    render() {
      if (!this.message) return;

      const holidays = this.holidays;
      const start = this.startDate;
      const transit = TRANSIT[this.dataset.country] || DEFAULT_TRANSIT;

      const productionMin = parseInt(this.dataset.productionMin, 10) || 0;
      const productionMax = parseInt(this.dataset.productionMax, 10) || 0;

      const min = addBusinessDays(start, productionMin + transit[0], holidays);
      const max = addBusinessDays(start, productionMax + transit[1], holidays);

      const minLabel = format(min);
      const maxLabel = format(max);

      const line =
        minLabel === maxLabel
          ? COPY.single.replace('{min}', minLabel)
          : COPY.range.replace('{min}', minLabel).replace('{max}', maxLabel);

      this.message.textContent = line;

      if (this.dataset.duties === 'true') {
        const duties = document.createElement('span');
        duties.className = 'delivery-eta__duties';
        duties.textContent = COPY.duties;
        this.message.appendChild(duties);
      }

      if (this.input) {
        this.input.value = minLabel === maxLabel ? minLabel : `${minLabel} – ${maxLabel}`;
      }
    }
  }

  customElements.define('delivery-eta', DeliveryEta);
})();
